import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth, API } from "../App";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Badge } from "../components/ui/badge";
import { Textarea } from "../components/ui/textarea";
import { 
  LayoutDashboard, FileQuestion, ClipboardList, BarChart3, 
  Shield, LogOut, Users, Plus, Edit, Trash2, UserPlus, Clock, Activity, MessageSquare, Bell, FileDown, Eye
} from "lucide-react";
import { generateExamReport } from "../utils/pdfGenerator";
import QuestionPreviewDialog from "./QuestionPreviewDialog";

const ExamManagement = () => {
  const { user, token, logout } = useAuth();
  const navigate = useNavigate();
  
  const [exams, setExams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [showAssignDialog, setShowAssignDialog] = useState(false);
  const [showOrganiserDialog, setShowOrganiserDialog] = useState(false);
  const [editingExam, setEditingExam] = useState(null);
  const [selectedExam, setSelectedExam] = useState(null);
  const [assignEmails, setAssignEmails] = useState("");
  const [organiserEmails, setOrganiserEmails] = useState("");
  const [sendingReminder, setSendingReminder] = useState(null);
  const [generatingPdf, setGeneratingPdf] = useState(null);
  const [previewExam, setPreviewExam] = useState(null);
  
  const [formData, setFormData] = useState({
    name: "",
    role: "BA",
    num_questions: 40,
    duration_minutes: 45,
    passing_score: 60,
    difficulty_mix: { easy: 15, medium: 15, hard: 10 },
    face_verification_required: true
  });

  useEffect(() => {
    fetchExams();
  }, []);

  const fetchExams = async () => {
    try {
      const response = await axios.get(`${API}/exams`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setExams(response.data);
    } catch (error) {
      toast.error("Failed to fetch exams");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    try {
      if (editingExam) {
        await axios.put(`${API}/exams/${editingExam.id}`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Exam updated successfully");
      } else {
        await axios.post(`${API}/exams`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Exam created successfully");
      }
      setShowDialog(false);
      resetForm();
      fetchExams();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save exam");
    }
  };

  const handleAssign = async () => {
    if (!selectedExam || !assignEmails.trim()) return;
    
    try {
      const emails = assignEmails.split(/[,\n]/).map(e => e.trim()).filter(e => e);
      await axios.post(`${API}/exams/${selectedExam.id}/assign`, emails, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Exam assigned to ${emails.length} candidates`);
      setShowAssignDialog(false);
      setAssignEmails("");
      fetchExams();
    } catch (error) {
      toast.error("Failed to assign exam");
    }
  };

  const handleAssignOrganisers = async () => {
    if (!selectedExam || !organiserEmails.trim()) return;
    try {
      const emails = organiserEmails.split(/[,\n]/).map(e => e.trim()).filter(e => e);
      await axios.post(`${API}/admin/exams/${selectedExam.id}/assign-organisers`, emails, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Assigned ${emails.length} organiser(s)`);
      setShowOrganiserDialog(false);
      setOrganiserEmails("");
      fetchExams();
    } catch (error) {
      toast.error("Failed to assign organisers");
    }
  };

  const openOrganiserDialog = (exam) => {
    setSelectedExam(exam);
    setOrganiserEmails("");
    setShowOrganiserDialog(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this exam?")) return;
    
    try {
      await axios.delete(`${API}/exams/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Exam deleted");
      fetchExams();
    } catch (error) {
      toast.error("Failed to delete exam");
    }
  };

  const openEditDialog = (exam) => {
    setEditingExam(exam);
    setFormData({
      name: exam.name,
      role: exam.role,
      num_questions: exam.num_questions,
      duration_minutes: exam.duration_minutes,
      passing_score: exam.passing_score,
      difficulty_mix: exam.difficulty_mix,
      face_verification_required: exam.face_verification_required !== false
    });
    setShowDialog(true);
  };

  const openAssignDialog = (exam) => {
    setSelectedExam(exam);
    setShowAssignDialog(true);
  };

  const sendReminder = async (exam) => {
    setSendingReminder(exam.id);
    try {
      const response = await axios.post(`${API}/admin/exams/${exam.id}/send-reminder`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const reminded = response.data.details?.filter(d => d.status === "reminded").length || 0;
      toast.success(`Reminders sent to ${reminded} candidate(s)`);
    } catch (error) {
      toast.error("Failed to send reminders");
    } finally {
      setSendingReminder(null);
    }
  };

  const downloadExamPdf = async (exam) => {
    setGeneratingPdf(exam.id);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const [resultsRes, topicRes] = await Promise.all([
        axios.get(`${API}/admin/results?exam_id=${exam.id}`, { headers }),
        axios.get(`${API}/admin/analytics/topic-performance?exam_id=${exam.id}`, { headers }).catch(() => ({ data: [] }))
      ]);
      generateExamReport(exam, resultsRes.data, topicRes.data);
      toast.success(`Report for "${exam.name}" downloaded!`);
    } catch (error) {
      console.error("PDF generation error:", error);
      toast.error("Failed to generate exam report");
    } finally {
      setGeneratingPdf(null);
    }
  };

  const resetForm = () => {
    setEditingExam(null);
    setFormData({
      name: "",
      role: "BA",
      num_questions: 40,
      duration_minutes: 45,
      passing_score: 60,
      difficulty_mix: { easy: 15, medium: 15, hard: 10 },
      face_verification_required: true
    });
  };

  const handleLogout = () => {
    logout();
    navigate("/auth");
  };

  const navItems = [
    { icon: LayoutDashboard, label: "Dashboard", path: "/admin" },
    { icon: FileQuestion, label: "Question Bank", path: "/admin/questions" },
    { icon: ClipboardList, label: "Exams", path: "/admin/exams", active: true },
    { icon: BarChart3, label: "Results", path: "/admin/results" },
    { icon: Users, label: "Organisers", path: "/admin/organisers" },
    { icon: MessageSquare, label: "Feedback", path: "/admin/feedback" },
    { icon: Activity, label: "IP Logs", path: "/admin/logs" }
  ];

  return (
    <div className="flex min-h-screen bg-slate-50" data-testid="exam-management">
      {/* Sidebar */}
      <aside className="sidebar">
        <div className="p-6">
          <div className="flex items-center gap-3 mb-8">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-indigo-600 to-blue-500 flex items-center justify-center">
              <Shield className="w-5 h-5 text-white" />
            </div>
            <div>
              <span className="text-lg font-bold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>edSolutions360</span>
              <p className="text-xs text-slate-500">Examination Portal</p>
            </div>
          </div>
          
          <nav className="space-y-2">
            {navItems.map((item) => (
              <div
                key={item.path}
                className={`nav-item ${item.active ? 'active' : ''}`}
                onClick={() => navigate(item.path)}
              >
                <item.icon className="w-5 h-5" />
                <span>{item.label}</span>
              </div>
            ))}
          </nav>
        </div>
        
        <div className="mt-auto p-6 border-t border-slate-100">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-lg bg-indigo-100 flex items-center justify-center">
              <Users className="w-5 h-5 text-indigo-600" />
            </div>
            <div>
              <p className="text-sm font-medium text-slate-900">{user?.full_name}</p>
              <p className="text-xs text-slate-500">{user?.email}</p>
            </div>
          </div>
          <Button variant="outline" className="w-full" onClick={handleLogout}>
            <LogOut className="w-4 h-4 mr-2" />
            Logout
          </Button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="main-content p-8">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>Exam Management</h1>
              <p className="text-slate-600 mt-1">{exams.length} exams configured</p>
            </div>
            <Button 
              onClick={() => { resetForm(); setShowDialog(true); }}
              className="bg-indigo-600 hover:bg-indigo-700"
              data-testid="create-exam-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Create Exam
            </Button>
          </div>

          {/* Exams List */}
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="spinner" />
            </div>
          ) : exams.length === 0 ? (
            <Card className="text-center py-12">
              <ClipboardList className="w-16 h-16 text-slate-300 mx-auto mb-4" />
              <h4 className="text-lg font-medium text-slate-900 mb-2">No Exams Created</h4>
              <p className="text-slate-500 mb-4">Create your first exam to start assessing candidates.</p>
              <Button onClick={() => setShowDialog(true)} className="bg-indigo-600 hover:bg-indigo-700">
                <Plus className="w-4 h-4 mr-2" />
                Create Exam
              </Button>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {exams.map((exam, index) => (
                <Card key={exam.id} className="card-hover" data-testid={`exam-card-${index}`}>
                  <CardHeader className="pb-4">
                    <div className="flex items-center justify-between">
                      <Badge variant="outline">
                        {exam.role === "BA" ? "Business Analyst" : exam.role === "Frontend" ? "Frontend Developer" : exam.role === "React Frontend" ? "React Frontend" : "MERN Developer"}
                      </Badge>
                      <div className="flex items-center gap-1">
                        <Button variant="ghost" size="icon" onClick={() => setPreviewExam(exam)} data-testid={`preview-exam-${index}`} title="Preview Questions">
                          <Eye className="w-4 h-4 text-indigo-600" />
                        </Button>
                        <Button variant="ghost" size="icon" onClick={() => openEditDialog(exam)} data-testid={`edit-exam-${index}`}>
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="icon" className="text-red-600" onClick={() => handleDelete(exam.id)} data-testid={`delete-exam-${index}`}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                    <CardTitle className="mt-2">{exam.name}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-slate-500">Questions</span>
                        <span className="font-medium">{exam.num_questions}</span>
                      </div>
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-slate-500">Duration</span>
                        <span className="font-medium flex items-center gap-1">
                          <Clock className="w-4 h-4" />
                          {exam.duration_minutes} min
                        </span>
                      </div>
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-slate-500">Passing Score</span>
                        <span className="font-medium">{exam.passing_score}%</span>
                      </div>
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-slate-500">Assigned Candidates</span>
                        <span className="font-medium">{exam.assigned_emails?.length || 0}</span>
                      </div>
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-slate-500">Assigned Organisers</span>
                        <span className="font-medium">{exam.assigned_organisers?.length || 0}</span>
                      </div>
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-slate-500">Face Verification</span>
                        <span className={`font-medium text-xs px-2 py-0.5 rounded-full ${exam.face_verification_required !== false ? 'bg-emerald-100 text-emerald-700' : 'bg-zinc-100 text-zinc-500'}`}>
                          {exam.face_verification_required !== false ? 'Required' : 'Disabled'}
                        </span>
                      </div>
                      <div className="pt-3 border-t border-slate-100">
                        <p className="text-xs text-slate-500 mb-2">Difficulty Mix:</p>
                        <div className="flex gap-2">
                          <span className="badge-easy px-2 py-1 rounded text-xs">Easy: {exam.difficulty_mix?.easy || 0}</span>
                          <span className="badge-medium px-2 py-1 rounded text-xs">Med: {exam.difficulty_mix?.medium || 0}</span>
                          <span className="badge-hard px-2 py-1 rounded text-xs">Hard: {exam.difficulty_mix?.hard || 0}</span>
                        </div>
                      </div>
                    </div>
                    <div className="flex gap-2 mt-4">
                      <Button 
                        className="flex-1 bg-indigo-600 hover:bg-indigo-700"
                        onClick={() => openAssignDialog(exam)}
                        data-testid={`assign-exam-${index}`}
                      >
                        <UserPlus className="w-4 h-4 mr-2" />
                        Assign
                      </Button>
                      <Button 
                        variant="outline"
                        className="flex-1"
                        onClick={() => sendReminder(exam)}
                        disabled={sendingReminder === exam.id}
                        data-testid={`remind-exam-${index}`}
                      >
                        <Bell className="w-4 h-4 mr-2" />
                        {sendingReminder === exam.id ? "Sending..." : "Remind"}
                      </Button>
                    </div>
                    <Button 
                      variant="outline"
                      className="w-full mt-2 border-emerald-200 text-emerald-600 hover:bg-emerald-50"
                      onClick={() => openOrganiserDialog(exam)}
                      data-testid={`assign-organiser-${index}`}
                    >
                      <Users className="w-4 h-4 mr-2" />
                      Assign Organisers
                    </Button>
                    <Button 
                      variant="outline"
                      className="w-full mt-2 border-indigo-200 text-indigo-600 hover:bg-indigo-50"
                      onClick={() => downloadExamPdf(exam)}
                      disabled={generatingPdf === exam.id}
                      data-testid={`download-exam-pdf-${index}`}
                    >
                      <FileDown className="w-4 h-4 mr-2" />
                      {generatingPdf === exam.id ? "Generating PDF..." : "Download Summary Report"}
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </main>

      {/* Create/Edit Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editingExam ? "Edit Exam" : "Create New Exam"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="form-group">
              <Label>Exam Name</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                placeholder="e.g., Business Analyst Assessment"
                data-testid="exam-name-input"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="form-group">
                <Label>Role</Label>
                <Select value={formData.role} onValueChange={(v) => setFormData({...formData, role: v})}>
                  <SelectTrigger data-testid="exam-role-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="BA">Business Analyst</SelectItem>
                    <SelectItem value="MERN">MERN Developer</SelectItem>
                    <SelectItem value="Frontend">Frontend Developer</SelectItem>
                    <SelectItem value="React Frontend">React Frontend (Advanced)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="form-group">
                <Label>Number of Questions</Label>
                <Input
                  type="number"
                  value={formData.num_questions}
                  onChange={(e) => setFormData({...formData, num_questions: parseInt(e.target.value)})}
                  data-testid="exam-questions-input"
                />
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="form-group">
                <Label>Duration (minutes)</Label>
                <Input
                  type="number"
                  value={formData.duration_minutes}
                  onChange={(e) => setFormData({...formData, duration_minutes: parseInt(e.target.value)})}
                  data-testid="exam-duration-input"
                />
              </div>
              <div className="form-group">
                <Label>Passing Score (%)</Label>
                <Input
                  type="number"
                  value={formData.passing_score}
                  onChange={(e) => setFormData({...formData, passing_score: parseInt(e.target.value)})}
                  data-testid="exam-passing-score-input"
                />
              </div>
            </div>
            
            <div className="form-group">
              <Label>Difficulty Mix</Label>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label className="text-xs text-slate-500">Easy</Label>
                  <Input
                    type="number"
                    value={formData.difficulty_mix.easy}
                    onChange={(e) => setFormData({
                      ...formData, 
                      difficulty_mix: {...formData.difficulty_mix, easy: parseInt(e.target.value)}
                    })}
                    data-testid="exam-easy-input"
                  />
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Medium</Label>
                  <Input
                    type="number"
                    value={formData.difficulty_mix.medium}
                    onChange={(e) => setFormData({
                      ...formData, 
                      difficulty_mix: {...formData.difficulty_mix, medium: parseInt(e.target.value)}
                    })}
                    data-testid="exam-medium-input"
                  />
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Hard</Label>
                  <Input
                    type="number"
                    value={formData.difficulty_mix.hard}
                    onChange={(e) => setFormData({
                      ...formData, 
                      difficulty_mix: {...formData.difficulty_mix, hard: parseInt(e.target.value)}
                    })}
                    data-testid="exam-hard-input"
                  />
                </div>
              </div>
            </div>

            {/* Face Verification Toggle */}
            <div className="flex items-center justify-between p-3 bg-zinc-50 rounded-lg border border-zinc-200">
              <div>
                <Label className="text-sm font-medium">Face Verification</Label>
                <p className="text-xs text-zinc-500 mt-0.5">
                  {formData.face_verification_required
                    ? "Candidates must verify identity via camera before starting the exam"
                    : "Candidates can directly attend the exam without face verification"}
                </p>
              </div>
              <button
                type="button"
                role="switch"
                aria-checked={formData.face_verification_required}
                onClick={() => setFormData({...formData, face_verification_required: !formData.face_verification_required})}
                className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out ${formData.face_verification_required ? 'bg-emerald-500' : 'bg-zinc-300'}`}
                data-testid="face-verification-toggle"
              >
                <span className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${formData.face_verification_required ? 'translate-x-5' : 'translate-x-0'}`} />
              </button>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)}>Cancel</Button>
            <Button onClick={handleSubmit} className="bg-indigo-600 hover:bg-indigo-700" data-testid="save-exam-btn">
              {editingExam ? "Update" : "Create"} Exam
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Assign Dialog */}
      <Dialog open={showAssignDialog} onOpenChange={setShowAssignDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Assign Candidates to {selectedExam?.name}</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <Label>Enter candidate email addresses (one per line or comma-separated)</Label>
            <Textarea
              value={assignEmails}
              onChange={(e) => setAssignEmails(e.target.value)}
              placeholder="candidate1@example.com&#10;candidate2@example.com"
              rows={6}
              className="mt-2"
              data-testid="assign-emails-input"
            />
            {selectedExam?.assigned_emails?.length > 0 && (
              <div className="mt-4">
                <Label className="text-xs text-slate-500">Currently assigned ({selectedExam.assigned_emails.length}):</Label>
                <div className="mt-2 flex flex-wrap gap-2">
                  {selectedExam.assigned_emails.slice(0, 5).map((email, idx) => (
                    <Badge key={idx} variant="secondary" className="text-xs">{email}</Badge>
                  ))}
                  {selectedExam.assigned_emails.length > 5 && (
                    <Badge variant="outline" className="text-xs">+{selectedExam.assigned_emails.length - 5} more</Badge>
                  )}
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAssignDialog(false)}>Cancel</Button>
            <Button onClick={handleAssign} className="bg-indigo-600 hover:bg-indigo-700" data-testid="confirm-assign-btn">
              <UserPlus className="w-4 h-4 mr-2" />
              Assign
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Assign Organisers Dialog */}
      <Dialog open={showOrganiserDialog} onOpenChange={setShowOrganiserDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Assign Organisers to {selectedExam?.name}</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <Label>Enter organiser email addresses (one per line or comma-separated)</Label>
            <Textarea
              value={organiserEmails}
              onChange={(e) => setOrganiserEmails(e.target.value)}
              placeholder="organiser@example.com"
              rows={4}
              className="mt-2"
              data-testid="assign-organiser-emails-input"
            />
            {selectedExam?.assigned_organisers?.length > 0 && (
              <div className="mt-4">
                <Label className="text-xs text-slate-500">Currently assigned ({selectedExam.assigned_organisers.length}):</Label>
                <div className="mt-2 flex flex-wrap gap-2">
                  {selectedExam.assigned_organisers.map((email, idx) => (
                    <Badge key={idx} variant="secondary" className="text-xs">{email}</Badge>
                  ))}
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowOrganiserDialog(false)}>Cancel</Button>
            <Button onClick={handleAssignOrganisers} className="bg-emerald-600 hover:bg-emerald-700" data-testid="confirm-assign-organisers-btn">
              <Users className="w-4 h-4 mr-2" />
              Assign Organisers
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Question Preview Dialog */}
      <QuestionPreviewDialog
        open={!!previewExam}
        onClose={() => setPreviewExam(null)}
        examId={previewExam?.id}
        examName={previewExam?.name}
      />
    </div>
  );
};

export default ExamManagement;
